import { buildQueryString, request, serializeCsvQuery } from "../client"
import type { ContactOrder } from "../contact-orders"
import type { KehrblattPlanningSettings } from "../planning"

export type ScheduleEventProjectionBundle = {
  work_orders: WorkOrder[]
  properties: Property[]
  customers?: Customer[]
  contact_points?: ContactPoint[]
  contact_orders: ContactOrder[]
  order_types: OrderType[]
  planning_settings?: KehrblattPlanningSettings | null
}

export type ScheduleEventTableProjection = ScheduleEventProjectionBundle & {
  items: ScheduleEvent[]
  total: number
}

export type CalendarBoardWorkOrderRef = {
  id: string
  can_manage: boolean
  organization_id?: string | null
  case_id?: string | null
  property_id?: string | null
  property_unit_id?: string | null
  property_unit_label?: string | null
  order_number?: string | null
  order_types?: WorkOrderOrderTypeItem[] | null
  estimated_duration_min?: number | null
  assigned_employee_id?: string | null
  current_schedule_event_id?: string | null
}

export type CalendarBoardPropertyRef = {
  id: string
  organization_id?: string | null
  street?: string | null
  house_number?: string | null
  zip?: string | null
  city?: string | null
  country?: string | null
  address_extra?: string | null
  geo_lat?: number | null
  geo_lng?: number | null
}

export type CalendarBoardExternalBusyInterval = {
  calendar_entry_id: string
  calendar_id: string
  employee_id: string
  start_at: string
  end_at: string
  all_day: boolean
}

export type CalendarBoardProjection = {
  events: ScheduleEvent[]
  external_busy_intervals: CalendarBoardExternalBusyInterval[]
  work_orders: CalendarBoardWorkOrderRef[]
  properties: CalendarBoardPropertyRef[]
  contact_orders: ContactOrder[]
  planning_settings?: KehrblattPlanningSettings | null
  route_transitions?: CalendarRouteTransition[]
  customers?: Customer[]
  contact_points?: ContactPoint[]
  order_types?: OrderType[]
}

export type CalendarRouteTransition = {
  id: string
  employee_id: string
  day: string
  sequence_index: number
  relation_kind:
    | "EVENT_TO_EVENT"
    | "EVENT_TO_FLEX_WINDOW"
    | "FLEX_WINDOW_TO_EVENT"
    | "FLEX_WINDOW_TO_FLEX_WINDOW"
    | string
  previous_event_id: string
  next_event_id: string
  previous_anchor_kind: "EVENT" | "FLEX_WINDOW" | string
  next_anchor_kind: "EVENT" | "FLEX_WINDOW" | string
  previous_anchor_start_at: string
  previous_anchor_end_at: string
  next_anchor_start_at: string
  next_anchor_end_at: string
  previous_event_type?: string
  next_event_type?: string
  previous_event_label?: string | null
  next_event_label?: string | null
  previous_work_order_number?: string | null
  next_work_order_number?: string | null
  gap_start_at: string
  gap_end_at: string
  gap_minutes: number
  overlap_minutes: number
  distance_km: number | null
  estimated_travel_minutes?: number | null
  rounded_travel_minutes?: number | null
  route_grade: "GREEN" | "YELLOW" | "RED" | "UNKNOWN" | string
  missing_coordinates: boolean
  reason: string
}

const scheduleEventQueryParams = (query?: ScheduleEventQuery) => ({
  organization_id: query?.organizationId,
  employee_id: query?.employeeId,
  work_order_id: query?.workOrderId,
  employee_ids: serializeCsvQuery(query?.employeeIds),
  work_order_ids: serializeCsvQuery(query?.workOrderIds),
  address_query: query?.addressQuery,
  search_query: query?.searchQuery,
  duration_bucket: query?.durationBucket,
  order_type_ids: serializeCsvQuery(query?.orderTypeIds),
  start_at_from: query?.startAtFrom,
  start_at_to: query?.startAtTo,
  status_in: query?.statusIn,
  offset: query?.offset,
  limit: query?.limit,
})

const normalizeNullableString = (value: string | null | undefined): string | null | undefined => {
  if (value === undefined) return undefined

  const normalized = (value ?? "").trim()

  return normalized || null
}

const normalizeConfirmedBy = (value: ScheduleEventCreatePayload["confirmed_by"]) => {
  if (value === undefined) return undefined

  if (value === null) return null

  return {
    customer_id: normalizeNullableString(value.customer_id) ?? null,
    contact_point_id: normalizeNullableString(value.contact_point_id) ?? null,
    name: normalizeNullableString(value.name) ?? null,
    email: normalizeNullableString(value.email) ?? null,
    phone: normalizeNullableString(value.phone) ?? null,
  }
}

const normalizeParticipantInput = (
  value: ScheduleEventParticipantInputPayload | null | undefined,
) => {
  if (value === undefined) return undefined

  if (value === null) return null

  return {
    display_name: normalizeNullableString(value.display_name) ?? null,
    customer_id: normalizeNullableString(value.customer_id) ?? null,
    contact_point_id: normalizeNullableString(value.contact_point_id) ?? null,
    email: normalizeNullableString(value.email) ?? null,
    phone: normalizeNullableString(value.phone) ?? null,
  }
}

const buildScheduleEventWritePayload = (
  payload: ScheduleEventCreatePayload | ScheduleEventUpdatePayload,
): ScheduleEventUpdatePayload => {
  const body: ScheduleEventUpdatePayload = {}

  if (payload.employee_id !== undefined)
    body.employee_id = normalizeNullableString(payload.employee_id) ?? null

  if (payload.event_type !== undefined) body.event_type = payload.event_type

  if (payload.flex_type !== undefined) body.flex_type = payload.flex_type

  if (payload.status !== undefined) body.status = payload.status

  if (payload.planning_flag !== undefined) body.planning_flag = payload.planning_flag

  if (payload.start_at !== undefined) body.start_at = payload.start_at

  if (payload.end_at !== undefined) body.end_at = payload.end_at

  if (payload.timeframe_start_at !== undefined)
    body.timeframe_start_at = normalizeNullableString(payload.timeframe_start_at) ?? null

  if (payload.timeframe_end_at !== undefined)
    body.timeframe_end_at = normalizeNullableString(payload.timeframe_end_at) ?? null

  if (payload.all_day !== undefined) body.all_day = payload.all_day

  if (payload.title !== undefined) body.title = normalizeNullableString(payload.title) ?? null

  if (payload.description !== undefined)
    body.description = normalizeNullableString(payload.description) ?? null

  if (payload.customer_note !== undefined)
    body.customer_note = normalizeNullableString(payload.customer_note) ?? null

  if (payload.location_text !== undefined)
    body.location_text = normalizeNullableString(payload.location_text) ?? null

  if (payload.property_id !== undefined)
    body.property_id = normalizeNullableString(payload.property_id) ?? null

  if (payload.work_order_id !== undefined)
    body.work_order_id = normalizeNullableString(payload.work_order_id) ?? null

  if (payload.source !== undefined) body.source = payload.source

  if (payload.organization_id !== undefined)
    body.organization_id = normalizeNullableString(payload.organization_id) ?? null

  if (payload.confirmed_by !== undefined)
    body.confirmed_by = normalizeConfirmedBy(payload.confirmed_by)

  if (payload.onsite_contact !== undefined)
    body.onsite_contact = normalizeParticipantInput(payload.onsite_contact)

  return body
}

const buildAcceptAppointmentRequestPayload = (
  payload: AcceptAppointmentRequestPayload,
): AcceptAppointmentRequestPayload => ({
  work_order_id: (payload.work_order_id ?? "").trim(),
  status: payload.status,
  confirmed_by: normalizeConfirmedBy(payload.confirmed_by) ?? null,
  onsite_contact: normalizeParticipantInput(payload.onsite_contact) ?? null,
})

export const fetchScheduleEvents = (query?: ScheduleEventQuery) =>
  request<ScheduleEvent[]>(`/schedule-events${buildQueryString(scheduleEventQueryParams(query))}`)

export const fetchScheduleEvent = (eventId: string) =>
  request<ScheduleEvent>(`/schedule-events/${eventId}`)

export type CalendarBoardRequestOptions = {
  signal?: AbortSignal
}

export type CompleteCalendarBoardRequestOptions = CalendarBoardRequestOptions & {
  pageSize?: number
}

export const fetchCalendarBoardProjection = (
  query?: ScheduleEventQuery,
  options: CalendarBoardRequestOptions = {},
) =>
  request<CalendarBoardProjection>(
    `/schedule-events${buildQueryString({
      projection: "calendar_board",
      ...scheduleEventQueryParams(query),
    })}`,
    { signal: options.signal },
  )

const appendUnique = <T>(
  target: T[],
  seen: Set<string>,
  items: T[] | null | undefined,
  keyFor: (item: T) => string,
): number => {
  let appended = 0
  for (const item of items ?? []) {
    const key = keyFor(item)

    if (!key || seen.has(key)) continue

    seen.add(key)
    target.push(item)
    appended += 1
  }

  return appended
}

const throwIfCalendarBoardRequestAborted = (signal?: AbortSignal) => {
  if (!signal?.aborted) return

  if (signal.reason instanceof Error) throw signal.reason

  throw new DOMException("Calendar board request aborted", "AbortError")
}

export const fetchCompleteCalendarBoardProjection = async (
  query: ScheduleEventQuery,
  options: CompleteCalendarBoardRequestOptions = {},
): Promise<CalendarBoardProjection> => {
  const requestedPageSize = Math.floor(options.pageSize ?? 5000)
  const pageSize = Math.min(5000, Math.max(1, requestedPageSize))
  let offset = Math.max(0, Math.floor(query.offset ?? 0))
  const result: CalendarBoardProjection = {
    events: [],
    external_busy_intervals: [],
    work_orders: [],
    properties: [],
    contact_orders: [],
    route_transitions: [],
    customers: [],
    contact_points: [],
    order_types: [],
    planning_settings: null,
  }
  const eventIds = new Set<string>()
  const externalBusyIds = new Set<string>()
  const workOrderIds = new Set<string>()
  const propertyIds = new Set<string>()
  const contactOrderIds = new Set<string>()
  const routeTransitionIds = new Set<string>()
  const customerIds = new Set<string>()
  const contactPointIds = new Set<string>()
  const orderTypeIds = new Set<string>()

  while (true) {
    throwIfCalendarBoardRequestAborted(options.signal)
    const page = await fetchCalendarBoardProjection(
      { ...query, offset, limit: pageSize },
      { signal: options.signal },
    )
    throwIfCalendarBoardRequestAborted(options.signal)
    const pageEvents = page.events ?? []
    const appendedEvents = appendUnique(result.events, eventIds, pageEvents, (item) =>
      String(item.id ?? ""),
    )
    appendUnique(
      result.external_busy_intervals,
      externalBusyIds,
      page.external_busy_intervals,
      (item) => String(item.calendar_entry_id ?? ""),
    )
    appendUnique(result.work_orders, workOrderIds, page.work_orders, (item) =>
      String(item.id ?? ""),
    )
    appendUnique(result.properties, propertyIds, page.properties, (item) => String(item.id ?? ""))
    appendUnique(result.contact_orders, contactOrderIds, page.contact_orders, (item) =>
      String(item.id ?? ""),
    )
    appendUnique(
      result.route_transitions ?? [],
      routeTransitionIds,
      page.route_transitions,
      (item) => String(item.id ?? ""),
    )
    appendUnique(result.customers ?? [], customerIds, page.customers, (item) =>
      String(item.id ?? ""),
    )
    appendUnique(result.contact_points ?? [], contactPointIds, page.contact_points, (item) =>
      String(item.id ?? ""),
    )
    appendUnique(result.order_types ?? [], orderTypeIds, page.order_types, (item) =>
      String(item.id ?? ""),
    )

    if (result.planning_settings === null && page.planning_settings !== undefined) {
      result.planning_settings = page.planning_settings
    }

    if (pageEvents.length < pageSize) return result

    if (!appendedEvents) {
      throw new Error("Kalenderdaten konnten nicht vollständig geladen werden.")
    }

    offset += pageSize
  }
}

export const fetchScheduleEventTable = (query?: ScheduleEventQuery) =>
  request<ScheduleEventTableProjection>(
    `/schedule-events${buildQueryString({
      projection: "table",
      ...scheduleEventQueryParams(query),
    })}`,
  )

export const createScheduleEvent = (payload: ScheduleEventCreatePayload) =>
  request<ScheduleEvent>("/schedule-events", {
    method: "POST",
    body: JSON.stringify(buildScheduleEventWritePayload(payload)),
  })

export type ScheduleEventBatchCreateResponse = {
  created: ScheduleEvent[]
}

export const createScheduleEventsBatch = (items: ScheduleEventCreatePayload[]) =>
  request<ScheduleEventBatchCreateResponse>("/schedule-events/batch-create", {
    method: "POST",
    body: JSON.stringify({
      items: items.map((item) => buildScheduleEventWritePayload(item)),
    }),
  })

export const updateScheduleEvent = (eventId: string, payload: ScheduleEventUpdatePayload) =>
  request<ScheduleEvent>(`/schedule-events/${eventId}`, {
    method: "PATCH",
    body: JSON.stringify(buildScheduleEventWritePayload(payload)),
  })

export const validateScheduleEventAction = (payload: ScheduleEventActionValidationRequest) =>
  request<ScheduleEventActionValidationResponse>("/schedule-events/validate-action", {
    method: "POST",
    body: JSON.stringify({
      operation: payload.operation,
      surface: payload.surface,
      event_id: normalizeNullableString(payload.event_id) ?? null,
      create_payload: payload.create_payload
        ? buildScheduleEventWritePayload(payload.create_payload)
        : null,
      update_payload: payload.update_payload
        ? buildScheduleEventWritePayload(payload.update_payload)
        : null,
    }),
  })

export const replaceScheduleEvent = (eventId: string, payload: ScheduleEventUpdatePayload) =>
  request<ScheduleEventReplaceResponse>(`/schedule-events/${eventId}/replace`, {
    method: "POST",
    body: JSON.stringify({
      update_payload: buildScheduleEventWritePayload(payload),
    }),
  })

export type AppointmentEmailOutcome = {
  status: "queued" | "skipped" | "not_requested"
  email_recipient?: string | null
  skipped_reason?: string | null
  detail?: string | null
}

export type ScheduleEventConfirmPayload = {
  send_confirmation_email?: boolean
}

export type ScheduleEventConfirmResponse = {
  event: ScheduleEvent
  email: AppointmentEmailOutcome
}

export const confirmAppointment = (
  eventId: string,
  payload: ScheduleEventConfirmPayload = {},
) =>
  request<ScheduleEventConfirmResponse>(`/schedule-events/${eventId}/confirm`, {
    method: "POST",
    body: JSON.stringify({
      send_confirmation_email: payload.send_confirmation_email ?? true,
    }),
  })

export type ScheduleEventReschedulePayload = {
  start_at: string
  end_at: string
  employee_id: string
  send_confirmation_email?: boolean
}

export type ScheduleEventRescheduleResponse = {
  canceled_event: ScheduleEvent
  created_event: ScheduleEvent
  email: AppointmentEmailOutcome
}

export const rescheduleAppointment = (
  eventId: string,
  payload: ScheduleEventReschedulePayload,
) =>
  request<ScheduleEventRescheduleResponse>(`/schedule-events/${eventId}/reschedule`, {
    method: "POST",
    body: JSON.stringify({
      start_at: payload.start_at,
      end_at: payload.end_at,
      employee_id: payload.employee_id,
      send_confirmation_email: payload.send_confirmation_email ?? true,
    }),
  })

export type ScheduleEventBatchUpdateItem = {
  event_id: string
  payload: ScheduleEventUpdatePayload
}

export type ScheduleEventBatchUpdateResponse = {
  updated: ScheduleEvent[]
}

export const updateScheduleEventsBatch = (items: ScheduleEventBatchUpdateItem[]) =>
  request<ScheduleEventBatchUpdateResponse>("/schedule-events/batch-update", {
    method: "POST",
    body: JSON.stringify({
      items: items.map((item) => ({
        event_id: item.event_id,
        payload: buildScheduleEventWritePayload(item.payload),
      })),
    }),
  })

export const acceptAppointmentRequest = (
  eventId: string,
  payload: AcceptAppointmentRequestPayload,
) =>
  request<ScheduleEvent>(`/schedule-events/${eventId}/appointment-request/accept`, {
    method: "POST",
    body: JSON.stringify(buildAcceptAppointmentRequestPayload(payload)),
  })

export const deleteScheduleEvent = (
  eventId: string,
  opts?: {
    reschedule?: boolean
    customerCommunicationMode?: AppointmentCustomerCommunicationMode | string | null
  },
) =>
  request<void>(
    `/schedule-events/${eventId}${buildQueryString({
      reschedule: opts?.reschedule || undefined,
      customer_communication_mode: opts?.customerCommunicationMode || undefined,
    })}`,
    { method: "DELETE" },
  )

export type ScheduleEventBulkConfirmItemPayload = {
  event_id: string
  onsite_contact?: ScheduleEventParticipantInputPayload | null
}

export type ScheduleEventBulkConfirmItem = {
  event_id: string
  status: "confirmed" | "already_confirmed" | "skipped" | string
  detail?: string | null
  event?: ScheduleEvent | null
}

export type ScheduleEventBulkConfirmResponse = {
  items: ScheduleEventBulkConfirmItem[]
}

export const confirmScheduleEventsBatch = (items: ScheduleEventBulkConfirmItemPayload[]) =>
  request<ScheduleEventBulkConfirmResponse>("/schedule-events/bulk-confirm", {
    method: "POST",
    body: JSON.stringify({
      items: items.map((item) => ({
        event_id: item.event_id.trim(),
        onsite_contact: normalizeParticipantInput(item.onsite_contact) ?? null,
      })),
    }),
  })

export type ScheduleEventConfirmationCommunicationItem = {
  event_id: string
  status: "queued" | "skipped" | string
  detail?: string | null
  email_recipient?: string | null
  communication_id?: string | null
  queue_item_id?: string | null
  skipped_reason?: string | null
  recipient_resolution_status?: string | null
  recipient_resolution_reason_code?: string | null
}

export type ScheduleEventConfirmationCommunicationResponse = {
  items: ScheduleEventConfirmationCommunicationItem[]
}

export const sendScheduleEventConfirmationCommunications = (eventIds: string[]) =>
  request<ScheduleEventConfirmationCommunicationResponse>(
    "/schedule-events/confirmation-communications",
    {
      method: "POST",
      body: JSON.stringify({
        items: eventIds
          .map((eventId) => eventId.trim())
          .filter(Boolean)
          .map((event_id) => ({ event_id })),
      }),
    },
  )

export const cancelScheduleEvent = (
  eventId: string,
  payload?: { target_status?: ScheduleEventStatus | string | null },
) =>
  request<ScheduleEvent>(`/schedule-events/${eventId}/cancel`, {
    method: "POST",
    body: JSON.stringify({
      target_status: payload?.target_status || undefined,
    }),
  })

export const resolveCancellationRecipients = (eventIds: string[]) =>
  request<ScheduleEventCancellationRecipientResolveResult>(
    "/schedule-events/cancellation-recipients/resolve",
    {
      method: "POST",
      body: JSON.stringify({
        event_ids: eventIds.map((eventId) => eventId.trim()).filter(Boolean),
      }),
    },
  )

export type CancelReplanTarget = {
  work_order_id: string
  event_id?: string | null
  status: "cancelable" | "skipped"
  detail?: string | null
  start_at?: string | null
  end_at?: string | null
  employee_id?: string | null
}

export type CancelReplanPreviewResponse = {
  items: CancelReplanTarget[]
}

export type CancelReplanCancelItem = {
  event_id: string
  status: string
  event?: ScheduleEvent | null
  detail?: string | null
  email_recipient?: string | null
  customer_communication_mode?: string | null
  recipient_resolution_status?: string | null
  recipient_resolution_reason_code?: string | null
}

export type CancelReplanCancelResponse = {
  items: CancelReplanCancelItem[]
}

export type AppointmentCustomerCommunicationResult = {
  schedule_event_id: string
  previous_schedule_event_id?: string | null
  work_order_id?: string | null
  communication_kind: string
  status: string
  communication_id?: string | null
  email_queued: boolean
  recipient_resolution_status?: string | null
  recipient_resolution_reason_code?: string | null
  skipped_reason?: string | null
}

export type AppointmentCustomerCommunicationBatchResult = {
  items: AppointmentCustomerCommunicationResult[]
}

export const previewCancelReplanWorkOrders = (payload: {
  organization_id: string
  work_order_ids: string[]
}) =>
  request<CancelReplanPreviewResponse>("/schedule-events/cancel-replan/preview", {
    method: "POST",
    body: JSON.stringify({
      organization_id: payload.organization_id,
      work_order_ids: payload.work_order_ids.map((id) => id.trim()).filter(Boolean),
    }),
  })

export const cancelReplanScheduleEvents = (eventIds: string[]) =>
  request<CancelReplanCancelResponse>("/schedule-events/cancel-replan/cancel", {
    method: "POST",
    body: JSON.stringify({
      event_ids: eventIds.map((eventId) => eventId.trim()).filter(Boolean),
    }),
  })
